<template>
  <teleport to="#form">
    <div v-if="showDialog" class="base">
      <div class="overlay" @click="$emit('close')"></div>
      <div class="content">
        <h2 class="font-bold mb-4">{{ update_mode ? 'タスクの更新' : '作業の追加' }}</h2>

        <!-- Two Column Grid Layout -->
        <div class="form-grid">
          <!-- Row 1 -->
          <div class="form-field">
            <label class="text-xs mb-1 block">案件名:</label>
            <input type="text" class="text-xs border rounded-lg px-4 py-2 field-width" :value="localForm.bill_name"
                   @click="openBillDialog"
                   readonly>
            <div v-if="errors?.bill_id" class="text-red-500 text-xs mt-1">
              {{ errors.bill_id }}
            </div>
          </div>

          <div class="form-field">
            <label class="text-xs mb-1 block">タスク:</label>
            <select
                v-model="localForm.greenc_task_id"
                class="text-xs border rounded-lg px-4 py-2 field-width"
                @change="onGreencTaskChange"
            >
              <option v-for="item in filteredGreencTasksList" :key="item.id" :value="parseInt(item.id)">
                {{ item.name }}
              </option>
            </select>
            <div v-if="errors?.greenc_task_id" class="text-red-500 text-xs mt-1">
              {{ errors.greenc_task_id }}
            </div>
          </div>

          <!--   New row 2       -->
          <div class="form-field">
            <label class="text-xs mb-1 block">作業:</label>
            <input @input="onNameChange" v-model="localForm.F1" type="text" class="text-xs border rounded-lg px-4 py-2 field-width">
            <div v-if="errors?.F1" class="text-red-500 text-xs mt-1">
              {{ errors.F1 }}
            </div>
          </div>

          <div class="form-field">
            <label class="text-xs mb-1 block">作業者:</label>
            <select
                v-model="localForm.user_id"
                class="text-xs border rounded-lg px-4 py-2 field-width"
                @change="onUserChange"
            >
              <option v-for="item in usersList" :key="item.id" :value="parseInt(item.id)">
                {{ item.name }}
              </option>
            </select>
            <div v-if="errors?.user_id" class="text-red-500 text-xs mt-1">
              {{ errors.bill_id }}
            </div>
          </div>

          <!-- Row 3 -->
          <div class="form-field">
            <label class="text-xs mb-1 block">予定または実績:</label>
            <select
                v-model="localForm.greenc_child_task_work_type_id"
                class="text-xs border rounded-lg px-4 py-2 field-width"
                @change="onGreencChildTaskWorkTypesChange"
            >
              <option v-for="item in greenc_child_task_work_typeList" :key="item.id" :value="parseInt(item.id)">
                {{ item.name }}
              </option>
            </select>
            <div v-if="errors?.greenc_child_task_work_type_id" class="text-red-500 text-xs mt-1">
              {{ errors.greenc_child_task_work_type_id }}
            </div>
          </div>

          <div class="form-field">
            <label class="text-xs mb-1 block">現場/書類:</label>
            <select
                v-model="localForm.work_type_id"
                class="text-xs border rounded-lg px-4 py-2 field-width"
                @change="onItemChange('work_type_id')"
            >
              <option v-for="item in work_typesList" :key="item.id" :value="parseInt(item.id)">
                {{ item.name }}
              </option>
            </select>
            <div v-if="errors?.work_type_id" class="text-red-500 text-xs mt-1">
              {{ errors.work_type_id }}
            </div>
          </div>

          <!-- Row 4 -->
          <div class="form-field">
            <label class="text-xs mb-1 block">時間帯:</label>
            <select
                v-model="localForm.work_time_type_id"
                class="text-xs border rounded-lg px-4 py-2 field-width"
                @change="onItemChange( 'work_time_type_id')"
            >
              <option v-for="item in work_time_typesList" :key="item.id" :value="parseInt(item.id)">
                {{ item.name }}
              </option>
            </select>
            <div v-if="errors?.work_time_type_id" class="text-red-500 text-xs mt-1">
              {{ errors.work_time_type_id }}
            </div>
          </div>

          <div class="form-field">
            <label class="text-xs mb-1 block">作業日:</label>
            <Datepicker
                :model-value="localForm.task_date"
                @update:model-value="onDateChange"
                date-picker
                locale="ja"
                class="text-xs field-width"
                auto-apply
                :enable-time-picker="false"
                :clearable="false"
                :format="formatDate"
            />
            <div v-if="errors?.task_date" class="text-red-500 text-xs mt-1">
              {{ errors.task_date }}
            </div>
          </div>

          <!-- Row 5 -->
          <div class="form-field">
            <label class="text-xs mb-1 block">作業開始時間:</label>
            <Datepicker
                :model-value="parseTime(localForm.start_time)"
                @update:model-value="(value) => onTimeChange(value, 'start_time')"
                time-picker
                :is-24="true"
                :minutes-increment="15"
                :minutes-grid-increment="15"
                placeholder="時間を選択してください"
                class="text-xs field-width"
                :action-row="{ showCancel: true, showSelect: true, showPreview: false }"
                cancel-text="キャンセル"
                select-text="選択"
                :clearable="false"
            />
            <div v-if="errors?.start_time" class="text-red-500 text-xs mt-1">
              {{ errors.start_time }}
            </div>
          </div>

          <div class="form-field">
            <label class="text-xs mb-1 block">作業終了時間:</label>
            <Datepicker
                :model-value="parseTime(localForm.end_time)"
                @update:model-value="(value) => onTimeChange(value, 'end_time')"
                time-picker
                :is-24="true"
                :minutes-increment="15"
                :minutes-grid-increment="15"
                placeholder="時間を選択してください"
                class="text-xs field-width"
                :action-row="{ showCancel: true, showSelect: true, showPreview: false }"
                cancel-text="キャンセル"
                select-text="選択"
                :clearable="false"
            />
            <div v-if="errors?.end_time" class="text-red-500 text-xs mt-1">
              {{ errors.end_time }}
            </div>
          </div>

          <!-- Row 6 - Full Width -->
          <div class="form-field full-width">
            <label class="text-xs mb-1 block">作業進捗:</label>
            <select
                v-model="localForm.greenc_mst_status_type_id"
                class="text-xs border rounded-lg px-4 py-2 field-width"
                @change="onStatusTypesChange"
            >
              <option v-for="item in greencMstStatusTypesList" :key="item.id" :value="parseInt(item.id)">
                {{ item.name }}
              </option>
            </select>
            <div v-if="errors?.greenc_mst_status_type_id" class="text-red-500 text-xs mt-1">
              {{ errors.greenc_mst_status_type_id }}
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div v-if="update_mode" class="flex items-center justify-start mt-6 gap-2">
          <button
              @click="$emit('close')"
              class="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg flex items-center"
          >
            <span class="font-bold text-xs">取消</span>
          </button>
          <button
              @click="$emit('update')"
              class="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg text-xs flex items-center gap-1"
          >
            <svg class="w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            <span class="text-xs font-bold text-white">登録</span>
          </button>
          <button
              @click="$emit('delete', form.id)"
              class="bg-red-500 hover:bg-red-700 text-white py-2 px-4 rounded-lg flex items-center gap-1"
          >
            <svg class="w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            <span class="text-xs font-bold text-white">消去</span>
          </button>
        </div>
        <div v-else class="flex items-start mt-6 gap-2">
          <button
              @click="$emit('close')"
              class="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded-lg flex items-center"
          >
            <span class="font-bold text-xs">取消</span>
          </button>
          <button
              @click="$emit('save')"
              class="bg-indigo-500 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-lg flex items-center gap-1"
          >
            <svg class="w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            <span class="font-bold text-xs">登録</span>
          </button>
        </div>
        <div v-if="apiErrorMsg" class="text-red-500 text-xs mt-2">
          {{ apiErrorMsg }}
        </div>
      </div>
      <div v-if="loading" class="spinner-overlay">
        <div class="spinner"></div>
      </div>
    </div>

    <CCommonDialogComponent
        ref="ref_bills_list_dialog"
        :data="dialogData"
        :headers="dialogHeaders"
        @row-click="rowClick"
    />
  </teleport>
</template>

<script>
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import CCommonDialogComponent from "@/components/CCommonDialogComponent.vue";
import billsFields from '@/fields/bills'

export default {
  name: 'CTimeEditForm',
  components: { Datepicker, CCommonDialogComponent },
  props: {
    showDialog: { type: Boolean, required: true },
    update_mode: { type: Boolean, required: true },
    form: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    apiErrorMsg: { type: String, default: '' },
    loading: { type: Boolean, default: false },
    billsList: { type: Array, default: () => [] },
    billsListAll: { type: Array, default: () => [] },
    usersList: { type: Array, default: () => [] },
    greencMstStatusTypesList: { type: Array, default: () => [] },
    greencTasksList: { type: Array, default: () => [] },
    greenc_child_task_work_typeList: { type: Array, default: () => [] },
    work_typesList: { type: Array, default: () => [] },
    work_time_typesList: { type: Array, default: () => [] },
  },
  emits: ['save', 'update', 'delete', 'close', 'change'],
  data() {
    return {
      localForm: { ...this.form },
      dialogData: [],
      dialogHeaders: [],
    }
  },
  computed: {
    filteredGreencTasksList() {
      return this.greencTasksList.filter(item => parseInt(item.bill_id) === parseInt(this.localForm.bill_id))
    }
  },
  watch: {
    form: {
      handler(newForm) {
        this.localForm = { ...newForm }
      },
      deep: true
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return ''
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    },
    parseTime(timeString) {
      if (!timeString) return null
      const [hours, minutes] = timeString.split(':')
      return {
        hours: parseInt(hours, 10),
        minutes: parseInt(minutes, 10)
      }
    },
    onTimeChange(timeValue, field) {
      const timeString = timeValue
          ? `${String(timeValue.hours).padStart(2, '0')}:${String(timeValue.minutes).padStart(2, '0')}`
          : ''
      this.localForm[field] = timeString
      this.$emit('change', { field, value: timeString })
    },
    onDateChange(dateValue) {
      const formattedDate = dateValue ? this.formatDate(dateValue) : ''
      this.localForm.task_date = formattedDate
      this.$emit('change', { field: 'task_date', value: formattedDate })
    },
    onBillChange() {
      this.$emit('change', { field: 'bill_id', value: this.localForm.bill_id })
    },
    onUserChange() {
      this.$emit('change', { field: 'user_id', value: this.localForm.user_id })
    },
    onStatusTypesChange() {
      this.$emit('change', { field: 'greenc_mst_status_type_id', value: this.localForm.greenc_mst_status_type_id })
    },
    onGreencTaskChange() {
      this.$emit('change', { field: 'greenc_task_id', value: this.localForm.greenc_task_id })
    },
    onGreencChildTaskWorkTypesChange() {
      this.$emit('change', { field: 'greenc_child_task_work_type_id', value: this.localForm.greenc_child_task_work_type_id })
    },
    onItemChange(fieldName) {
      this.$emit('change', { field: fieldName, value: this.localForm[fieldName] })
    },
    onNameChange() {
      this.$emit('change', { field: 'F1', value: this.localForm.F1 })
    },
    openBillDialog() {
      this.dialogData = this.billsListAll
      this.dialogHeaders = this.extractViewFields(billsFields)
      this.$refs.ref_bills_list_dialog.openDialog()
    },
    extractViewFields(data) {
      return data.filter(item => item.view === true)
    },
    rowClick(row) {
      this.$emit('change', { field: 'bill_id', value: row.id })
      this.$emit('change', { field: 'bill_name', value: row.name })
      this.$refs.ref_bills_list_dialog.closeDialog()
    }
  }
}
</script>

<style scoped>
.base {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
}
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
}
.content {
  background-color: white;
  position: relative;
  border-radius: 10px;
  padding: 32px;
  z-index: 2;
  max-width: 550px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Two Column Grid Layout */
.form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.field-width {
  width: 100%;
}

.spinner-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: center;
}
.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 2s linear infinite;
}
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Responsive: Stack on smaller screens */
@media (max-width: 768px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .content {
    padding: 24px;
  }
}
</style>